import { Popper, PopperPlacementType } from "@mui/base/Popper";
import { ClickAwayListener } from "@mui/material";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import {
  Children,
  ComponentType,
  isValidElement,
  memo,
  ReactNode,
  useState,
} from "react";
import { Flex } from "./flex";
import { InspectorConfig } from "./inspector";
import * as styles from "./inspector-v2.module.css";
import {
  LabeledInput,
  LabelOrientation,
  LabelPosition,
  LabelSize,
} from "./labeled-input";
import { Text } from "./text";

export interface InspectorComponentProps<T> {
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
}

export type ValidatableInspector<T, C = undefined> = {
  errors: (object: T, context: C) => Promise<string[]>;
} & (
  | { config: InspectorConfig<T> }
  | { Component: ComponentType<InspectorComponentProps<T>> }
);

interface InspectorLayoutProps {
  children: ReactNode;
  title?: string;
}

export const InspectorLayout = memo(function InspectorLayout({
  children,
  title,
}: InspectorLayoutProps) {
  const childArray = Children.toArray(children);

  return (
    <Flex dir="column" gap="3xl" w="full">
      {title && (
        <Text fontSize="md" fontWeight="medium">
          {title}
        </Text>
      )}
      {childArray.map((child, index) => {
        const key = isValidElement(child) ? child.key : index;
        return (
          <Flex
            borderBottomWidth={
              index < childArray.length - 1 ? "1px" : undefined
            }
            borderColor="primary"
            borderStyle="solid"
            dir="column"
            key={key ?? index}
            pb={index < childArray.length - 1 ? "3xl" : undefined}
          >
            {child}
          </Flex>
        );
      })}
    </Flex>
  );
});

interface InspectorSectionProps {
  label?: string;
  description?: string;
  children: ReactNode;
  defaultOpen?: boolean;
}

export const InspectorSection = memo(function InspectorSection({
  label,
  description,
  children,
  defaultOpen = true,
}: InspectorSectionProps) {
  if (!label) {
    return (
      <Flex dir="column" gap="xl">
        {children}
      </Flex>
    );
  }

  return (
    <details
      className={styles.details}
      open={defaultOpen}
      style={{ width: "100%" }}
    >
      <Flex
        align="center"
        as="summary"
        dir="row"
        justify="space-between"
        style={{ cursor: "pointer", listStyle: "none", width: "100%" }}
      >
        <Flex dir="column" gap="xs">
          <Text fontSize="sm" fontWeight="medium">
            {label}
          </Text>
          {description && (
            <Text fontSize="xs" textColor="tertiary">
              {description}
            </Text>
          )}
        </Flex>
        <ChevronDownIcon height={16} width={16} />
      </Flex>
      <Flex dir="column" gap="xl" pb="xs" pt="xl">
        {children}
      </Flex>
    </details>
  );
});

interface InspectorFieldProps {
  label?: ReactNode | null;
  description?: string;
  children: ReactNode;
  labelPosition?: "above" | "left" | "right";
  spaceBetween?: boolean;
}

export const InspectorField = memo(function InspectorField({
  label,
  description,
  children,
  labelPosition = "above",
  spaceBetween,
}: InspectorFieldProps) {
  if (label === null) {
    return <>{children}</>;
  }

  const positionMap = {
    above: LabelPosition.ABOVE,
    left: LabelPosition.LEFT,
    right: LabelPosition.RIGHT,
  };

  return (
    <LabeledInput
      alignmentOverride={
        labelPosition === "right" || labelPosition === "left"
          ? "center"
          : undefined
      }
      description={description}
      label={label}
      orientation={LabelOrientation.VERTICAL}
      position={positionMap[labelPosition]}
      size={LabelSize.EXTRA_SMALL}
      spaceBetween={spaceBetween}
    >
      {children}
    </LabeledInput>
  );
});

interface InspectorPopoverProps {
  trigger: ReactNode;
  children: ReactNode;
  placement?: PopperPlacementType;
}

const POPOVER_MIN_WIDTH = 249;

export const InspectorPopover = memo(function InspectorPopover({
  trigger,
  children,
  placement = "top-end",
}: InspectorPopoverProps) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLDivElement | null>(null);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div>
        <div onClick={() => setOpen(!open)} ref={setAnchor}>
          {trigger}
        </div>
        {open && (
          <Popper
            anchorEl={anchor}
            open={open}
            placement={placement}
            style={{ zIndex: 1400 }}
          >
            <Flex
              borderColor="primary"
              borderStyle="solid"
              borderWidth="1px"
              className={styles.inspectorPopover}
              dir="column"
              p="lg"
              radius="md"
              style={{ minWidth: POPOVER_MIN_WIDTH }}
            >
              {children}
            </Flex>
          </Popper>
        )}
      </div>
    </ClickAwayListener>
  );
});
